import { useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Tooltip,
  Upload,
  Image,
  message,
} from "antd";
import {
  InfoCircleOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import imageCompression from "browser-image-compression";

interface AddProductModalProps {
  visible: boolean;
  onCancel: () => void;
  onAdd: (product: {
    name: string;
    price: number;
    attributes: string[];
    description: string;
    productLink: string;
    images: string[];
  }) => void;
}

export default function AddProductModal({
  visible,
  onCancel,
  onAdd,
}: AddProductModalProps) {
  const [form] = Form.useForm();
  const [imageList, setImageList] = useState<string[]>([]);

  // Función auxiliar para convertir un archivo a base64
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      onAdd({ ...values, images: imageList });
      form.resetFields();
      setImageList([]);
      onCancel();
    });
  };

  // Usamos beforeUpload para procesar y comprimir el archivo antes de convertirlo a base64.
  const beforeUpload = async (file: File) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Solo se pueden subir imágenes.");
      return Upload.LIST_IGNORE;
    }

    try {
      // Opciones de compresión:
      const options = {
        maxSizeMB: 1, // Tamaño máximo en MB (ajusta según tus necesidades)
        maxWidthOrHeight: 1920, // Máxima dimensión (ancho o alto)
        useWebWorker: true,
        initialQuality: 1,
      };

      // Comprimir la imagen
      const compressedFile = await imageCompression(file, options);

      // Convertir la imagen comprimida a base64
      const base64 = await getBase64(compressedFile);
      setImageList((prevList) => [...prevList, base64]);
    } catch (error) {
      message.error("Error al comprimir la imagen.");
      console.error(error);
    }

    // Retornamos false para prevenir la carga automática
    return false;
  };

  // Función para eliminar la imagen en una posición dada
  const handleRemoveImage = (indexToRemove: number) => {
    setImageList((prevList) => prevList.filter((_, index) => index !== indexToRemove));
  };

  return (
    <>
      {/* CSS para las animaciones */}
      <style>
        {`
          .fade-enter {
            opacity: 0;
            transform: scale(0.95);
          }
          .fade-enter-active {
            opacity: 1;
            transform: scale(1);
            transition: opacity 300ms, transform 300ms;
          }
          .fade-exit {
            opacity: 1;
            transform: scale(1);
          }
          .fade-exit-active {
            opacity: 0;
            transform: scale(0.95);
            transition: opacity 300ms, transform 300ms;
          }
        `}
      </style>

      <Modal
        title="Agregar Producto"
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            Cancelar
          </Button>,
          <Button key="add" type="primary" onClick={handleOk}>
            Agregar
          </Button>,
        ]}
      >
        <Form layout="vertical" form={form}>
          {/* Nombre del Producto */}
          <Form.Item
            label={
              <>
                Nombre del Producto
                <span style={{ marginLeft: "5px" }}>
                  <Tooltip title="Ingrese el nombre del producto (Ej: Laptop, Zapatos, Celular)">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              </>
            }
            name="name"
            rules={[{ required: true, message: "Ingrese el nombre del producto" }]}
          >
            <Input placeholder="Ej: Laptop" />
          </Form.Item>

          {/* Precio */}
          <Form.Item
            label={
              <>
                Precio
                <span style={{ marginLeft: "5px" }}>
                  <Tooltip title="Ingrese el precio en pesos colombianos, sin símbolos (Ej: 100000)">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              </>
            }
            name="price"
            rules={[{ required: true, message: "Ingrese el precio del producto" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              // Formatter para mostrar en COP con separador de miles (punto)
              formatter={(value) =>
                value
                  ? `COP ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                  : ""
              }
              // Parser para quitar caracteres no numéricos y convertir a número
              parser={(value) => (value ? Number(value.replace(/[^\d]/g, "")) : 0)}
              placeholder="Ej: 100000"
            />
          </Form.Item>

          {/* Atributos (Color, Tamaño, etc.) */}
          <Form.Item
            label={
              <>
                Características
                <span style={{ marginLeft: "5px" }}>
                  <Tooltip title="Añada características como color, tamaño, material, etc.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              </>
            }
            name="attributes"
          >
            <Select mode="tags" placeholder="Ej: Rojo, M, Algodón" tokenSeparators={[","]} />
          </Form.Item>

          {/* Descripción */}
          <Form.Item
            label={
              <>
                Descripción
                <span style={{ marginLeft: "5px" }}>
                  <Tooltip title="Proporcione una breve descripción del producto">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              </>
            }
            name="description"
          >
            <Input.TextArea placeholder="Ej: Laptop de alta gama con 16GB RAM" />
          </Form.Item>

          {/* Enlace del Producto */}
          <Form.Item
            label={
              <>
                Enlace del Producto
                <span style={{ marginLeft: "5px" }}>
                  <Tooltip title="Ingrese la URL donde los usuarios pueden comprar este producto">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              </>
            }
            name="productLink"
          >
            <Input placeholder="Ej: https://mi-tienda.com/producto" />
          </Form.Item>

          {/* Subida de Imágenes */}
          <Form.Item
            label={
              <>
                Imágenes
                <span style={{ marginLeft: "5px" }}>
                  <Tooltip title="Suba imágenes del producto para su previsualización">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              </>
            }
          >
            <Upload
              listType="picture"
              beforeUpload={beforeUpload}
              multiple
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Subir Imagen</Button>
            </Upload>

            {/* Separación mediante un div con margen superior para la previsualización */}
            <div style={{ marginTop: "10px" }}>
              <TransitionGroup style={{ display: "flex", gap: 10 }}>
                {imageList.map((img, index) => (
                  <CSSTransition key={index} timeout={300} classNames="fade">
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <Image width={80} height={80} src={img} style={{ borderRadius: 5 }} />
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveImage(index)}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          background: "rgba(255,255,255,0.7)",
                          borderRadius: "50%",
                          padding: "2px",
                          margin: "2px",
                        }}
                      />
                    </div>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}